import React, { useEffect } from "react";
import { DatePicker, Select, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  useAddTenorContracts,
  useAddTenorStyles,
  useAddTenorPeriodTypes,
  useAddTenorTenorOptions,
} from "hooks/useAddTenorCatalog";
import {
  buildAddTenorCatalogParams,
  createAddTenorDerivedFieldReset,
} from "constants/addTenorTable";
import {
  pickSelectedMissingTenorOption,
  resolveMonthsInTenor,
  resolveVisibleExpiryCalendarLink,
} from "helpers/Pricer/optionRowTenorCreation";

function CellWrap({ children }) {
  return <div className="add-tenor-cell">{children}</div>;
}

function ExcelText({ children }) {
  return <span className="add-tenor-cell__text">{children}</span>;
}

export function ReadOnlyFieldCell({ value, rowErrors, showDash = false }) {
  return (
    <CellWrap rowErrors={rowErrors}>
      {showDash || !value ? (
        <span className="add-tenor-cell__dash">—</span>
      ) : (
        <ExcelText>{value}</ExcelText>
      )}
    </CellWrap>
  );
}

const selectPopup = (dark) => (dark ? "dropdown-dark" : "");
const datePickerPopup = (dark) => (dark ? "date-picker-dropdown-dark" : "");
const datePickerClass = (dark) =>
  dark ? "add-tenor-excel-picker date-picker-dark" : "add-tenor-excel-picker";

/** Only show Ant Select loading when parent fields are set and a fetch is in flight. */
function cascadeSelectLoading(prerequisiteMet, isFetching) {
  return Boolean(prerequisiteMet && isFetching);
}

function selectOptionLabel(options, value) {
  const id = Number(value);
  if (!Number.isFinite(id)) return "";
  return options.find((item) => Number(item.value) === id)?.label ?? "";
}

export function RowProductSelect({
  row,
  updateRow,
  productOptions,
  loading,
  disabled,
  rowErrors,
  dark_Theme,
}) {
  if (disabled) {
    return (
      <CellWrap rowErrors={rowErrors}>
        <ExcelText>{row.product_label || "—"}</ExcelText>
      </CellWrap>
    );
  }
  return (
    <CellWrap rowErrors={rowErrors}>
      <Select
        className="add-tenor-excel-select"
        showSearch
        optionFilterProp="label"
        loading={loading}
        placeholder="product"
        options={productOptions}
        value={row.product_id ?? undefined}
        popupClassName={selectPopup(dark_Theme)}
        onChange={(value, opt) =>
          updateRow(row.key, {
            product_id: value,
            product_label: opt?.label ?? "",
            contract_id: null,
            contract_label: "",
            style_id: null,
            style_label: "",
            style_display: "",
            period_type_id: null,
            period_label: "",
            ...createAddTenorDerivedFieldReset(),
          })
        }
      />
    </CellWrap>
  );
}

export function RowContractSelect({ row, updateRow, disabled, rowErrors, dark_Theme }) {
  const productReady = row.product_id != null;
  const { data, isFetching } = useAddTenorContracts(row.product_id);
  const options = data?.options ?? [];
  const defaults = data?.defaults ?? {};

  useEffect(() => {
    if (!productReady || row.contract_id != null || defaults.contract_id == null) {
      return;
    }
    const contract_id = Number(defaults.contract_id);
    if (!Number.isFinite(contract_id)) return;
    const contract_label = selectOptionLabel(options, contract_id);
    if (!contract_label) return;
    updateRow(row.key, {
      contract_id,
      contract_label,
      style_id: null,
      style_label: "",
      style_display: "",
      period_type_id: null,
      period_label: "",
      ...createAddTenorDerivedFieldReset(),
    });
  }, [
    productReady,
    row.contract_id,
    row.key,
    defaults.contract_id,
    options,
    updateRow,
  ]);

  if (disabled) {
    return (
      <CellWrap rowErrors={rowErrors}>
        <ExcelText>{row.contract_label || "—"}</ExcelText>
      </CellWrap>
    );
  }
  return (
    <CellWrap rowErrors={rowErrors}>
      <Select
        className="add-tenor-excel-select"
        showSearch
        optionFilterProp="label"
        loading={cascadeSelectLoading(productReady, isFetching)}
        placeholder="Contract"
        options={productReady ? options : []}
        value={row.contract_id ?? undefined}
        disabled={!productReady}
        popupClassName={selectPopup(dark_Theme)}
        onChange={(value, opt) =>
          updateRow(row.key, {
            contract_id: value,
            contract_label: opt?.label ?? "",
            style_id: null,
            style_label: "",
            style_display: "",
            period_type_id: null,
            period_label: "",
            ...createAddTenorDerivedFieldReset(),
          })
        }
      />
    </CellWrap>
  );
}

export function RowStyleSelect({
  row,
  updateRow,
  disabled,
  rowErrors,
  dark_Theme,
}) {
  const styleReady = row.product_id != null && row.contract_id != null;
  const { data, isFetching } = useAddTenorStyles(
    row.product_id,
    row.contract_id
  );
  const options = data?.options ?? [];
  const defaults = data?.defaults ?? {};

  useEffect(() => {
    if (!styleReady || row.style_id != null || defaults.style_id == null) {
      return;
    }
    const style_id = Number(defaults.style_id);
    if (!Number.isFinite(style_id)) return;
    const style_label = selectOptionLabel(options, style_id);
    if (!style_label) return;
    updateRow(row.key, {
      style_id,
      style_label,
      style_display: style_label,
      period_type_id: null,
      period_label: "",
      ...createAddTenorDerivedFieldReset(),
    });
  }, [
    styleReady,
    row.style_id,
    row.key,
    defaults.style_id,
    options,
    updateRow,
  ]);

  if (disabled) {
    return (
      <CellWrap rowErrors={rowErrors}>
        <ExcelText>{row.style_display || row.style_label || "—"}</ExcelText>
      </CellWrap>
    );
  }
  return (
    <CellWrap rowErrors={rowErrors}>
      <Select
        className="add-tenor-excel-select"
        showSearch
        optionFilterProp="label"
        loading={cascadeSelectLoading(styleReady, isFetching)}
        placeholder="Style"
        options={styleReady ? options : []}
        value={row.style_id ?? undefined}
        disabled={!styleReady}
        popupClassName={selectPopup(dark_Theme)}
        onChange={(value, opt) =>
          updateRow(row.key, {
            style_id: value,
            style_label: opt?.label ?? "",
            style_display: opt?.label ?? "",
            period_type_id: null,
            period_label: "",
            ...createAddTenorDerivedFieldReset(),
          })
        }
      />
    </CellWrap>
  );
}

export function RowPeriodSelect({ row, updateRow, rowErrors, dark_Theme }) {
  const catalogParams = buildAddTenorCatalogParams(row);
  const styleReady = catalogParams != null;
  const { data, isFetching } = useAddTenorPeriodTypes(catalogParams);
  const options = data?.options ?? [];
  const defaults = data?.defaults ?? {};

  useEffect(() => {
    if (!styleReady || row.period_type_id != null || defaults.period_id == null) {
      return;
    }
    const period_type_id = Number(defaults.period_id);
    if (!Number.isFinite(period_type_id)) return;
    const period_label = selectOptionLabel(options, period_type_id);
    if (!period_label) return;
    updateRow(row.key, {
      period_type_id,
      period_label,
      ...createAddTenorDerivedFieldReset(),
    });
  }, [
    styleReady,
    row.period_type_id,
    row.key,
    defaults.period_id,
    options,
    updateRow,
  ]);

  return (
    <CellWrap rowErrors={rowErrors}>
      <Select
        className="add-tenor-excel-select"
        showSearch
        optionFilterProp="label"
        loading={cascadeSelectLoading(styleReady, isFetching)}
        placeholder="Period"
        options={styleReady ? options : []}
        value={row.period_type_id ?? undefined}
        disabled={!styleReady}
        popupClassName={selectPopup(dark_Theme)}
        onChange={(value, opt) =>
          updateRow(row.key, {
            period_type_id: value,
            period_label: opt?.label ?? "",
            ...createAddTenorDerivedFieldReset(),
          })
        }
      />
    </CellWrap>
  );
}

export function TenorSelectCell({
  row,
  updateRow,
  onTenorSelect,
  onMonthsChange,
  rowErrors,
  shortcutLabel,
  dark_Theme,
}) {
  const catalogParams = buildAddTenorCatalogParams(row);
  const periodReady = catalogParams != null && row.period_type_id != null;
  const { options, isLoading, availabilityHint, defaultSublegs } =
    useAddTenorTenorOptions(
      catalogParams,
      row.period_type_id,
      row.period_label
    );

  useEffect(() => {
    if (!periodReady || isLoading || row.sublegs_from_api) return;
    const months = resolveMonthsInTenor(row, null, defaultSublegs);
    if (row.months_in_tenor === months) return;
    if (onMonthsChange) {
      onMonthsChange(row.key, months);
      return;
    }
    updateRow(row.key, { months_in_tenor: months, sub_leg: "", sub_legs: [] });
  }, [
    periodReady,
    isLoading,
    defaultSublegs,
    row.sublegs_from_api,
    row.months_in_tenor,
    row.key,
    row.period_label,
    onMonthsChange,
    updateRow,
  ]);

  const tenorDisabled =
    !periodReady || (!isLoading && !options.length);
  const emptyTenorCopy = availabilityHint || "No tenors";

  return (
    <CellWrap rowErrors={rowErrors}>
      <Tooltip
        title={
          tenorDisabled && !isLoading && availabilityHint
            ? availabilityHint
            : ""
        }
      >
        <Select
          className="add-tenor-excel-select"
          allowClear
          showSearch
          optionFilterProp="value"
          optionLabelProp="value"
          loading={cascadeSelectLoading(periodReady, isLoading)}
          placeholder={
            !periodReady
              ? "Period"
              : isLoading
                ? "Loading…"
                : options.length
                  ? "Tenor"
                  : "No tenors"
          }
          value={row.tenor_label || undefined}
          options={periodReady ? options : []}
          disabled={tenorDisabled}
          popupClassName={selectPopup(dark_Theme)}
          notFoundContent={emptyTenorCopy}
          onChange={(value, option) => {
            if (!value) {
              updateRow(row.key, {
                ...createAddTenorDerivedFieldReset(),
                enable_start_date: row.enable_start_date,
              });
              return;
            }
            const selected = pickSelectedMissingTenorOption(
              options,
              value,
              option
            );
            onTenorSelect(row.key, selected, defaultSublegs);
          }}
        />
      </Tooltip>
    </CellWrap>
  );
}

export function ExpiryCalendarLinkCell({ row, isContinuation }) {
  const catalogParams = buildAddTenorCatalogParams(row);
  const periodReady = catalogParams != null && row.period_type_id != null;
  const { expiryCalendarLinks, options } = useAddTenorTenorOptions(
    catalogParams,
    row.period_type_id,
    row.period_label
  );
  const selectedTenorLink = periodReady
    ? options.find((item) => item.value === row.tenor_label)?.link
    : null;
  const visibleLink = resolveVisibleExpiryCalendarLink(
    expiryCalendarLinks,
    selectedTenorLink
  );

  if (isContinuation || !periodReady || !visibleLink) {
    return <CellWrap />;
  }

  return (
    <CellWrap>
      <Tooltip title={visibleLink.url}>
        <a
          className="add-tenor-cell__link"
          href={visibleLink.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${visibleLink.label}`}
        >
          {visibleLink.label}
        </a>
      </Tooltip>
    </CellWrap>
  );
}

export function UlTenorSelectCell({ row, updateRow, rowErrors, dark_Theme }) {
  const catalogParams = buildAddTenorCatalogParams(row);
  const periodReady = catalogParams != null && row.period_type_id != null;
  const { ulOptions, isLoading } = useAddTenorTenorOptions(
    catalogParams,
    row.period_type_id,
    row.period_label
  );
  const options = periodReady ? ulOptions : [];

  return (
    <CellWrap rowErrors={rowErrors}>
      <Select
        className="add-tenor-excel-select"
        showSearch
        optionFilterProp="label"
        loading={cascadeSelectLoading(periodReady, isLoading)}
        placeholder={
          !periodReady
            ? "Period"
            : isLoading
              ? "Loading…"
              : options.length
                ? "UL Tenor"
                : "No missing UL tenors"
        }
        value={row.ul_tenor || undefined}
        options={options}
        disabled={!periodReady || !row.tenor_label || (!isLoading && !options.length)}
        popupClassName={selectPopup(dark_Theme)}
        notFoundContent="No missing UL tenors"
        onChange={(v) => updateRow(row.key, { ul_tenor: v ?? "" })}
      />
    </CellWrap>
  );
}

export function SubLegsCell({ row, rowErrors }) {
  const months = Math.max(1, Number(row.months_in_tenor) || 1);

  if (months <= 1) {
    return (
      <CellWrap rowErrors={rowErrors}>
        <span className="add-tenor-cell__dash">—</span>
      </CellWrap>
    );
  }

  const subLeg = String(row.sub_leg ?? "").trim();

  return (
    <CellWrap rowErrors={rowErrors}>
      <span className="add-tenor-sub-leg-cell">
        {subLeg ? (
          <ExcelText>{subLeg}</ExcelText>
        ) : (
          <span className="add-tenor-cell__dash">—</span>
        )}
      </span>
    </CellWrap>
  );
}

export function StartDateCell({ row, updateRow, rowErrors, dark_Theme }) {
  const catalogParams = buildAddTenorCatalogParams(row);
  const periodReady = catalogParams != null && row.period_type_id != null;
  const { enableStartDate, isLoading } = useAddTenorTenorOptions(
    catalogParams,
    row.period_type_id,
    row.period_label
  );

  useEffect(() => {
    if (!periodReady || isLoading) return;
    if (row.enable_start_date === enableStartDate) return;
    updateRow(row.key, {
      enable_start_date: enableStartDate,
      start_date: enableStartDate ? row.start_date : null,
    });
  }, [
    periodReady,
    isLoading,
    enableStartDate,
    row.enable_start_date,
    row.start_date,
    row.key,
    updateRow,
  ]);

  if (!row.enable_start_date) {
    return (
      <CellWrap rowErrors={rowErrors}>
        <span className="add-tenor-cell__dash">—</span>
      </CellWrap>
    );
  }

  return (
    <CellWrap rowErrors={rowErrors}>
      <DatePicker
        className={datePickerClass(dark_Theme)}
        value={row.start_date ? dayjs(row.start_date) : null}
        format="YYYY-MM-DD"
        placeholder="Start Date"
        onChange={(d) => updateRow(row.key, { start_date: d })}
        popupClassName={datePickerPopup(dark_Theme)}
      />
    </CellWrap>
  );
}

export function EndDateCell({ row, updateRow, rowErrors, dark_Theme }) {
  return (
    <CellWrap rowErrors={rowErrors}>
      <DatePicker
        className={datePickerClass(dark_Theme)}
        value={row.end_date ? dayjs(row.end_date) : null}
        format="YYYY-MM-DD"
        placeholder="End Date"
        onChange={(d) => updateRow(row.key, { end_date: d })}
        popupClassName={datePickerPopup(dark_Theme)}
      />
    </CellWrap>
  );
}

